/**
 * Regalos por importe (custom, sustituye a Wi BOGO) – SantoStudio 2026-10-02
 *
 * Mantiene en el carrito un regalo por cada tramo alcanzado (config en snippets/gift-tiers.liquid):
 * - lo añade al superar el importe y lo quita al bajar de él;
 * - fuerza cantidad 1 y elimina duplicados;
 * - el total que cuenta para los tramos excluye los propios regalos.
 * El precio 0 € lo aplica un descuento automático nativo de Shopify por tramo.
 * - SALVAGUARDA (21/10/2026, incidencia KIM10): si un regalo NO sale a 0 € (p. ej. un código de descuento que
 *   no se combina con el descuento automático del regalo), se quita del carrito y no se vuelve a añadir en
 *   esta sesión. Así nunca se cobra un regalo y el cliente puede quitarlo sin que reaparezca.
 * Avisa a Horizon con el evento estándar shopify:cart:lines-update para que el drawer,
 * la página de carrito y el contador se actualicen como con cualquier otro cambio.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const CONFIG_ID = 'GiftTiersConfig';
const LOG = '[gift-tiers]';

/** Eventos que lanzamos nosotros: no deben volver a disparar la sincronización. */
const ownEvents = new WeakSet();
const FAILED_KEY = 'gift-tiers-failed';

/** Variantes que no deben (re)añadirse en esta sesión: rechazadas por Shopify (sin stock…) o que salen cobradas. */
const failedVariants = new Set(loadFailed());

function loadFailed() {
  try {
    return JSON.parse(window.sessionStorage.getItem(FAILED_KEY) || '[]');
  } catch (error) {
    return [];
  }
}

function markFailed(variantId) {
  failedVariants.add(variantId);
  try {
    window.sessionStorage.setItem(FAILED_KEY, JSON.stringify([...failedVariants]));
  } catch (error) {
    /* sin sessionStorage: solo vale para esta página */
  }
}

let running = false;
let pending = false;
let timer = null;

function readConfig() {
  const el = document.getElementById(CONFIG_ID);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (error) {
    console.warn(LOG, 'Config no válida', error);
    return null;
  }
}

function routeRoot() {
  return window.Shopify?.routes?.root || '/';
}

function sectionIds() {
  const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  return [...new Set(ids)];
}

async function requestJSON(path, body) {
  const response = await fetch(`${routeRoot()}${path}`, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
    cache: 'no-store',
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || (data && data.status && data.status >= 400)) {
    const error = new Error(data?.description || data?.message || `HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return data;
}

/**
 * Calcula qué hay que cambiar en el carrito para que los regalos cuadren con los tramos.
 * @param {any} cart - respuesta de /cart.js
 * @param {{property: string, tiers: Array<{id: string, threshold: number, variantId: number, available: boolean}>}} config
 */
function plan(cart, config) {
  const prop = config.property;
  const isGift = (item) => Boolean(item.properties && item.properties[prop]);

  // Los productos de regalo no cuentan para el mínimo (igual que en los descuentos de Shopify),
  // ni como regalo ni si el cliente los compra por su cuenta.
  const giftProductIds = new Set(config.tiers.map((tier) => String(tier.productId)));
  const qualifyingTotal = cart.items
    .filter((item) => !isGift(item) && !giftProductIds.has(String(item.product_id)))
    .reduce((sum, item) => sum + item.final_line_price, 0);

  // Salvaguarda: un regalo que no sale a 0 € no debe quedarse en el carrito ni volver a añadirse.
  for (const item of cart.items.filter(isGift)) {
    const tier = config.tiers.find((t) => t.id === item.properties[prop]);
    if (tier && item.final_line_price > 0) {
      markFailed(tier.variantId);
      console.warn(LOG, `El regalo ${tier.id} sale cobrado (${item.final_line_price} cént.): se retira.`);
    }
  }

  const desired = config.tiers.filter(
    (tier) => tier.available && !failedVariants.has(tier.variantId) && qualifyingTotal >= tier.threshold
  );
  const desiredIds = new Set(desired.map((tier) => tier.id));

  /** @type {Record<string, number>} */
  const updates = {};
  const kept = new Set();

  for (const item of cart.items.filter(isGift)) {
    const tierId = item.properties[prop];
    const tier = config.tiers.find((t) => t.id === tierId);
    const valid =
      tier && desiredIds.has(tierId) && String(item.variant_id) === String(tier.variantId) && !kept.has(tierId);

    if (!valid) {
      updates[item.key] = 0;
      continue;
    }

    kept.add(tierId);
    if (item.quantity !== 1) updates[item.key] = 1;
  }

  const additions = desired.filter((tier) => !kept.has(tier.id));
  return { updates, additions, prop };
}

async function sync() {
  const config = readConfig();
  if (!config?.tiers?.length) return;

  if (running) {
    pending = true;
    return;
  }
  running = true;

  try {
    const cart = await requestJSON('cart.js');
    const { updates, additions, prop } = plan(cart, config);
    const updateKeys = Object.keys(updates);
    if (!updateKeys.length && !additions.length) return;

    // Avisamos a Horizon antes de tocar el carrito, igual que hacen sus propios componentes.
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = additions.length
      ? new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: additions.map((tier) => ({ merchandiseId: String(tier.variantId), quantity: 1 })),
          promise: deferred.promise,
        })
      : new CartLinesUpdateEvent({
          action: updateKeys.every((key) => updates[key] === 0) ? 'remove' : 'update',
          context: 'cart',
          lines: updateKeys.map((key) => ({ id: key, quantity: updates[key] })),
          promise: deferred.promise,
        });
    ownEvents.add(event);
    document.dispatchEvent(event);

    try {
      if (updateKeys.length) {
        await requestJSON('cart/update.js', { updates });
      }

      for (const tier of additions) {
        try {
          await requestJSON('cart/add.js', {
            items: [{ id: tier.variantId, quantity: 1, properties: { [prop]: tier.id } }],
          });
        } catch (error) {
          markFailed(tier.variantId);
          console.warn(LOG, `No se ha podido añadir el regalo ${tier.id}:`, error.message);
        }
      }

      // Una última llamada devuelve el carrito final y el HTML de las secciones del carrito.
      const finalCart = await requestJSON('cart/update.js', {
        sections: sectionIds().join(','),
        sections_url: window.location.pathname,
      });

      // Tras añadir regalos, se vuelve a revisar el carrito para confirmar que salen a 0 €.
      if (additions.length) pending = true;

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(finalCart),
        detail: {
          sections: finalCart.sections,
          items: finalCart.items,
          itemCount: finalCart.item_count,
          source: 'gift-tiers',
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      console.warn(LOG, error);
    }
  } catch (error) {
    console.warn(LOG, error);
  } finally {
    running = false;
    if (pending) {
      pending = false;
      schedule();
    }
  }
}

function schedule(delay = 250) {
  clearTimeout(timer);
  timer = setTimeout(sync, delay);
}

document.addEventListener(
  'shopify:cart:lines-update',
  (event) => {
    if (ownEvents.has(event)) return;
    const done = event.promise && typeof event.promise.then === 'function' ? event.promise : Promise.resolve();
    done
      .then((result) => {
        if (result?.detail?.didError) return;
        schedule();
      })
      .catch(() => {});
  },
  true
);

// Al cargar la página: por si el carrito ya viene descuadrado (otra pestaña, carrito antiguo…).
schedule(800);
